import { subject, type MongoAbility } from '@casl/ability';
import type { SessionUser } from '@movo/contracts/api/auth';
import type { SanitizedUser } from '@movo/contracts/api/users';
import type { UserRole } from '@movo/contracts/domain/user';
import { ROLE_OPTIONS, type RoleOption } from './roles';

// Gli amministratori si creano e si cambiano fuori da questa pagina (API o
// database): qui il ruolo admin non si assegna e non si toglie.
const ASSIGNABLE_ROLE_OPTIONS = ROLE_OPTIONS.filter(({ value }) => value !== 'admin');

export type RowPermissions = {
  isSelf: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canChangeRole: boolean;
  roleOptions: RoleOption[];
};

const userSubject = (tenantId: string | null, role: UserRole) =>
  subject('User', { tenantId, role });

/** Cosa l'utente collegato può fare sugli utenti, dalle regole CASL della sua ability. */
export function userPermissions(
  ability: MongoAbility,
  currentUser: Pick<SessionUser, '_id' | 'tenantId'> | null
) {
  const currentTenantId = currentUser?.tenantId ?? null;
  const rolesFor = (action: string, tenantId: string | null, options = ROLE_OPTIONS) =>
    options.filter(({ value }) => ability.can(action, userSubject(tenantId, value)));

  return {
    readableRoles: rolesFor('read', currentTenantId),
    creatableRoles: rolesFor('create', currentTenantId, ASSIGNABLE_ROLE_OPTIONS),

    /** Le azioni su una riga: sulla propria nessuna, il ruolo admin resta com'è; `roleOptions` solo se il ruolo si cambia. */
    forRow({
      _id,
      tenantId,
      role,
    }: Pick<SanitizedUser, '_id' | 'tenantId' | 'role'>): RowPermissions {
      const isSelf = _id === currentUser?._id;
      const canUpdate = !isSelf && ability.can('update', userSubject(tenantId, role));
      const canDelete = !isSelf && ability.can('delete', userSubject(tenantId, role));
      const canChangeRole = canUpdate && role !== 'admin';
      const roleOptions = canChangeRole
        ? rolesFor('update', tenantId, ASSIGNABLE_ROLE_OPTIONS)
        : [];
      return { isSelf, canUpdate, canDelete, canChangeRole, roleOptions };
    },
  };
}
